/***
 *
 *   TABLE
 *   Dynmaic table with sorting, search and actions.
 *   Header rows are created dynamically from column names unless specified.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/table
 *   https://ui.shadcn.com/docs/components/table
 *   https://ui.shadcn.com/docs/components/data-table
 *
 *   PROPS
 *   actions: [{ label: string, icon: string, callback: function, global: bool, globalOnly: bool } (array, optional)
 *   badge: add a badge to every row col { col: string, color: string, condition: { value, color } (object, optional)
 *   data: array of table rows [{ id, email, date_created }] or [{{ label, url }, email, date_created }] (array, required)
 *   footer: footer row { span: integer, value: string } (object, optional)
 *   header: array of header column names [string, string] (array, optional)
 *   hide: column names to hide [string, string] (array, optional)
 *   loading: toggle loading spinner (boolean, optional)
 *   searchable: show the search field (boolean, optional)
 *   selectable: enable the user to select table rows and perform global actions (boolean, optional)
 *   show: columns to show [string, string] (array, optional, default: all)
 *   translation: reference to a locale object to use for the header translations (string, optional)
 *
 **********/

import {
  Fragment,
  forwardRef,
  useState,
  useEffect,
  useCallback,
  isValidElement,
} from "react";
import { Loader, Search, Icon, cn, useTranslation } from "components/lib";
import { TableHeader } from "./header.jsx";
import { TableBody } from "./body.jsx";
import { TableFooter } from "./footer.jsx";
import { GlobalActions } from "./actions.jsx";

const Table = forwardRef(
  (
    {
      className,
      header,
      data,
      footer,
      caption,
      show,
      hide,
      children,
      actions,
      translation,
      loading,
      badge,
      selectable,
      searchable,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    // state
    const [rows, setRows] = useState([]); // normal rows
    const [filter, setFilter] = useState(false); // filtered rows
    const [selected, setSelected] = useState([]); // selected rows
    const [sort, setSort] = useState({ col: null, direction: null }); // sort state

    // set row state
    useEffect(() => {
      if (data) setRows(data);
    }, [data]);

    // update a row
    const editRowCallback = useCallback(
      (row) => {
        // update state
        const updatedRows = rows.map((r) =>
          r.id === row.id ? { ...r, ...row } : r
        );

        // update filter
        if (filter?.length)
          setFilter(
            updatedRows.filter((r) => filter.find((f) => f.id === r.id))
          );

        // return the new state for the parent to update its state
        return updatedRows;
      },
      [rows, filter]
    );

    // delete a row
    const deleteRowCallback = useCallback(
      (row) => {
        const removeIds = Array.isArray(row) ? row.map((r) => r.id) : [row.id];
        const updatedRows = rows.filter((r) => !removeIds.includes(r.id));

        // remove selected ids
        const updatedSelected = selected.filter(
          (s) => !removeIds.includes(s.id)
        );
        setSelected(updatedSelected);

        // update filter
        if (filter)
          setFilter(
            updatedRows.filter((r) => filter.find((f) => f.id === r.id))
          );

        // return the new state for the parent to update its state
        return updatedRows;
      },
      [rows, filter]
    );

    // sort the rows
    const sortRows = useCallback(
      (column) => {
        const rowsToSort = [...(filter.length ? filter : rows)];
        const direction =
          sort.col === column && sort.direction === "asc" ? "desc" : "asc";

        rowsToSort.sort((a, b) => {
          const valA = a[column]?.badge ? a[column].label : a[column];
          const valB = b[column]?.badge ? b[column].label : b[column];

          if (valA != null && valB != null) {
            if (
              /^\d{4}-\d{2}-\d{2}$|^\d{1,2} [A-Z][a-z]{2} \d{4}$/.test(valA)
            ) {
              return direction === "desc"
                ? new Date(valB) - new Date(valA)
                : new Date(valA) - new Date(valB);
            } else {
              return direction === "desc"
                ? valA > valB
                  ? -1
                  : valA < valB
                  ? 1
                  : 0
                : valA < valB
                ? -1
                : valA > valB
                ? 1
                : 0;
            }
          }

          return 0;
        });

        filter.length ? setFilter(rowsToSort) : setRows(rowsToSort);
        setSort({ col: column, direction: direction });
      },
      [rows, filter, sort]
    );

    // select an individaul row
    const selectRow = useCallback(
      (index, id) => {
        // toggle the select state of each row (save index, id)
        const s = [...selected];
        const i = s.findIndex((x) => x.index === index);
        i > -1 ? s.splice(i, 1) : s.push({ index, id });
        return setSelected(s);
      },
      [selected]
    );

    // select all rows from header
    const selectAllRows = useCallback(() => {
      // toggle all visible rows
      setSelected(
        selected.length ? [] : data.map((x, i) => ({ index: i, id: x.id }))
      );
    }, [data, selected]);

    const search = useCallback(
      (term) => {
        // search each cell in each row &
        // update state to show only filtered rows
        const rowsToShow = rows.filter((row) =>
          Object.values(row).some((cell) => {
            if (typeof cell === "object" && cell !== null) {
              return Object.values(cell).some((subCell) =>
                subCell?.toString().toLowerCase().includes(term.toLowerCase())
              );
            }
            return cell?.toString().toLowerCase().includes(term.toLowerCase());
          })
        );

        setFilter(rowsToShow);
      },
      [rows]
    );

    // loading
    if (loading) {
      return (
        <div className="relative w-full h-24 flex flex-col items-center justify-center">
          <Loader />
        </div>
      );
    }

    // empty table
    if (!rows.length) {
      return (
        <div>
          <div className="flex flex-row gap-x-2 w-full mb-3">
            {searchable && <Search callback={search} />}

            <GlobalActions
              actions={actions}
              selected={selected}
              editRowCallback={editRowCallback}
              deleteRowCallback={deleteRowCallback}
            />
          </div>
          <div className="relative w-full h-40 flex flex-col items-center justify-center">
            <Icon name="inbox" />
            <span>{t("global.table.empty")}</span>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="flex flex-row gap-x-2 w-full mb-3">
          {searchable && <Search callback={search} />}

          <GlobalActions
            actions={actions}
            selected={selected}
            editRowCallback={editRowCallback}
            deleteRowCallback={deleteRowCallback}
          />
        </div>

        <div className="relative w-full overflow-x-auto border rounded border-white/10">
          <table
            ref={ref}
            className={cn("w-full caption-bottom table-auto", className)}
            {...props}
          >
            {children || (
              <Fragment>
                {caption && <TableCaption>{caption}</TableCaption>}

                <TableHeader
                  header={header}
                  row={data[0]}
                  show={show}
                  hide={hide}
                  select={selectable && selectAllRows}
                  actions={actions?.length}
                  translation={translation}
                  sort={{ ...sort, callback: sortRows }}
                />

                <TableBody
                  show={show}
                  hide={hide}
                  badge={badge}
                  actions={actions}
                  selected={selected}
                  rows={filter || rows}
                  select={selectable && selectRow}
                  editRowCallback={editRowCallback}
                  deleteRowCallback={deleteRowCallback}
                />

                {footer && <TableFooter row={footer} />}
              </Fragment>
            )}
          </table>
        </div>

        {selected.length ? (
          <div className="flex-1 text-slate-500 mt-3">
            {selected.length} of {data.length}{" "}
            {data.length > 1 ? "rows" : "row"} selected
          </div>
        ) : undefined}
      </div>
    );
  }
);

Table.displayName = "Table";

const TableRow = forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-white/10 transition-all duration-200 sm:hover:bg-white/5 sm:hover:backdrop-blur-sm data-[state=selected]:bg-white/10 data-[state=selected]:backdrop-blur-md",
      className
    )}
    {...props}
  />
));

TableRow.displayName = "TableRow";

const TableCell = forwardRef(
  ({ className, label, children, ...props }, ref) => {
    const isImage = isValidElement(children) && children.props.src; // ignore labels on images

    return (
      <td
        data-label={label}
        ref={ref}
        className={cn(
          "sm:before:content-none align-middle px-4 sm:p-4 [&:has([role=checkbox])]:pr-0",
          !isImage &&
            label &&
            " before:inline-block before:content-[attr(data-label)] before:w-24 before:mr-4 before:text-sm before:text-slate-400",
          className
        )}
        {...props}
      >
        {children || <span className="text-slate-400">-</span>}
      </td>
    );
  }
);

TableCell.displayName = "TableCell";

const TableCaption = forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-slate-500 dark:text-slate-400", className)}
    {...props}
  />
));

TableCaption.displayName = "TableCaption";

export { Table, TableRow, TableCell, TableCaption };
